"use client";

import { Fragment, JSX, useState } from "react";
import { type Translate, useViewerLanguage } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { MODULE_LABEL_WRAP } from "./module-label-budget";
import { useHoverCapable } from "@/lib/hooks/use-hover-capable";
import { SwipeScroll } from "@/components/charts/swipe-scroll";
import type {
  ModuleComparisonEntity,
  ModuleComparisonMetric,
  ModuleSplitComparisonData,
} from "@/types/modules";
import { stripInlineMarkdown } from "@/utils/inline-markdown";
import { importsTokenScope } from "./imports-token-scope";

type MetricValueType = "numeric" | "text" | "boolean";
type MetricValue = number | string | boolean;

function metricType(
  metric: ModuleComparisonMetric,
  entities: ModuleComparisonEntity[],
): MetricValueType {
  if (metric.type) return metric.type;
  const first = metric.values[entities[0]?.id ?? ""];
  if (typeof first === "number") return "numeric";
  if (typeof first === "boolean") return "boolean";
  return "text";
}

function computeBest(
  metric: ModuleComparisonMetric,
  entities: ModuleComparisonEntity[],
): boolean[] {
  const type = metricType(metric, entities);
  const values = entities.map((e) => metric.values[e.id]);

  if (type === "numeric") {
    const nums = values as number[];
    const best = metric.higherIsBetter ? Math.max(...nums) : Math.min(...nums);
    const count = nums.filter((v) => v === best).length;
    if (count === 1) return nums.map((v) => v === best);
    return values.map(() => false);
  }
  if (type === "text") {
    if (!metric.preferred) return values.map(() => false);
    return entities.map((e) => metric.preferred === e.id);
  }
  if (type === "boolean") {
    const trues = values.filter((v) => v === true).length;
    if (trues === 1) return values.map((v) => v === true);
  }
  return values.map(() => false);
}

function detailLine(
  metric: ModuleComparisonMetric,
  type: MetricValueType,
  t: Translate,
): string {
  if (type !== "numeric") return "";
  const parts: string[] = [];
  if (metric.unit) parts.push(metric.unit);
  parts.push(metric.higherIsBetter ? t("higher is better") : t("lower is better"));
  return parts.join(" · ");
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 14 14"
      fill="none"
      className={className}
      aria-hidden
    >
      <path
        d="M 3 7.2 L 5.8 10 L 11 4"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ValueCell({
  type,
  value,
  isBest,
}: {
  type: MetricValueType;
  value: MetricValue;
  isBest: boolean;
}) {
  const valueColor = isBest ? "var(--module-accent)" : "var(--fg)";
  const valueWeight = isBest ? 500 : 400;

  if (type === "numeric") {
    return (
      <span
        className="text-[16px] tabular-nums leading-none"
        style={{ color: valueColor, fontWeight: valueWeight }}
      >
        {value as number}
      </span>
    );
  }
  if (type === "text") {
    const strValue = String(value);
    return (
      <span
        className="text-[14px] leading-tight break-words"
        style={{ color: valueColor, fontWeight: valueWeight }}
        title={strValue}
      >
        {stripInlineMarkdown(strValue)}
      </span>
    );
  }
  const boolValue = value === true;
  return boolValue ? (
    <CheckIcon
      className={isBest ? "text-[var(--module-accent)]" : "text-[var(--fg)]"}
    />
  ) : (
    <span className="text-[var(--subtle)] text-[16px] leading-none">—</span>
  );
}

export default function ModuleSplitComparison({
  data,
  grayscale,
  accent,
  className,
}: {
  data: ModuleSplitComparisonData;
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const { t } = useViewerLanguage();
  const entities = data.entities;
  const metrics = data.metrics;
  const [hoveredCol, setHoveredCol] = useState<string | null>(null);
  const hasHover = useHoverCapable();

  const gridTemplateColumns = `minmax(120px, max-content) repeat(${metrics.length}, minmax(120px, max-content))`;

  return (
    <div
      className={cn("w-full", className)}
      style={importsTokenScope(grayscale, accent)}
    >
      <SwipeScroll className="pb-2 [mask-image:linear-gradient(to_right,black_0%,black_93%,transparent_100%)] [-webkit-mask-image:linear-gradient(to_right,black_0%,black_93%,transparent_100%)]">
        <div
          className="grid w-max"
          style={{ gridTemplateColumns }}
        >
          <div aria-hidden />
          {metrics.map((m) => {
            const detail = detailLine(m, metricType(m, entities), t);
            const dim = hoveredCol !== null && hoveredCol !== m.id;
            return (
              <div
                key={`h-${m.id}`}
                className={cn(
                  "flex flex-col gap-0.5 min-w-0 px-3 pb-2 border-l border-dashed border-[var(--border-dashed-strong)]",
                  dim && "opacity-40",
                )}
                style={{ transition: "opacity 0.15s" }}
                onMouseEnter={hasHover ? () => setHoveredCol(m.id) : undefined}
                onMouseLeave={hasHover ? () => setHoveredCol(null) : undefined}
              >
                <div
                  className={cn(
                    "text-[13px] font-medium text-[var(--fg)] leading-tight",
                    MODULE_LABEL_WRAP.cell,
                  )}
                >
                  {stripInlineMarkdown(m.label)}
                </div>
                {detail && (
                  <div
                    className={cn(
                      "text-[11px] text-[var(--subtle)] leading-tight",
                      MODULE_LABEL_WRAP.cell,
                    )}
                  >
                    {detail}
                  </div>
                )}
              </div>
            );
          })}

          {entities.map((e, ei) => {
            const isFirst = ei === 0;
            return (
              <Fragment key={e.id}>
                <div
                  className={cn(
                    "flex flex-col justify-center gap-0.5 min-w-0 px-3 py-3",
                    !isFirst && "border-t border-dashed border-[var(--border-dashed-strong)]",
                  )}
                >
                  <span
                    className={cn(
                      "text-[13px] font-medium text-[var(--fg)] leading-tight",
                      MODULE_LABEL_WRAP.cell,
                    )}
                  >
                    {stripInlineMarkdown(e.name)}
                  </span>
                  {e.tag && (
                    <span
                      className={cn(
                        "text-[11px] text-[var(--subtle)] leading-tight",
                        MODULE_LABEL_WRAP.cell,
                      )}
                    >
                      {stripInlineMarkdown(e.tag)}
                    </span>
                  )}
                </div>
                {metrics.map((m) => {
                  const type = metricType(m, entities);
                  const bestFlags = computeBest(m, entities);
                  const dim = hoveredCol !== null && hoveredCol !== m.id;
                  return (
                    <div
                      key={`${e.id}-${m.id}`}
                      className={cn(
                        "flex items-center min-w-0 px-3 py-3 border-l border-dashed border-[var(--border-dashed-strong)]",
                        !isFirst && "border-t border-dashed border-[var(--border-dashed-strong)]",
                        dim && "opacity-40",
                      )}
                      style={{ transition: "opacity 0.15s" }}
                      onMouseEnter={hasHover ? () => setHoveredCol(m.id) : undefined}
                      onMouseLeave={hasHover ? () => setHoveredCol(null) : undefined}
                    >
                      <ValueCell
                        type={type}
                        value={m.values[e.id]}
                        isBest={bestFlags[ei] ?? false}
                      />
                    </div>
                  );
                })}
              </Fragment>
            );
          })}
        </div>
      </SwipeScroll>
    </div>
  );
}
